import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React from "react";
import get from "lodash/get";
import { InfiniteScroll } from "./infinite-scroll.js";
import { removeDuplicateStories } from '../utils';
/**
 * This component can be used to implement InfiniteScroll on the story page. You will need to specify the function which renders the story (which will recieve props.index and props.story), and functions for triggering analytics.
 *
 * Example
 * ```javascript
 * import React from 'react';
 *
 * import { BlankStory } from './story-templates';
 * import { InfiniteStoryBase } from '@quintype/components';
 *
 * function StoryPageBase({index, story, otherProp}) {
 *   // Can switch to a different template based story-template, or only show a spoiler if index > 0
 *   return <BlankStory story={story} />
 * }
 *
 * const FIELDS = "id,headline,slug,url,hero-image-s3-key,hero-image-metadata,first-published-at,last-published-at,alternative,published-at,author-name,author-id,sections,story-template,tags,cards";
 * function storyPageLoadItems(pageNumber) {
 *   return global.superagent
 *            .get("/api/v1/stories", {fields: FIELDS, limit:5, offset:5*pageNumber})
 *            .then(response => response.body.stories.map(story => ({story: story, otherProp: "value"})));
 * }
 *
 * export function StoryPage(props) {
 *   return <InfiniteStoryBase {...props}
 *                             render={StoryPageBase}
 *                             loadItems={storyPageLoadItems}
 *                             onItemFocus={(item) => console.log(`Story In View: ${item.story.headline}`)}
 *                             onInitialItemFocus={(item) => console.log(`Do Analytics ${item.story.headline}`)} />
 * }
 *
 * ```
 *
 * #### Not changing the URL on every page
 * When the next story is focussed, the url and title of the page will be set to the next story. If this is not required, it can be disabled by setting the prop doNotChangeUrl={true}.
 * This is typically used when showing the original story, followed by previews of subsequent stories.
 *
 * Example:
 * ```javascript
 *   <InfiniteStoryBase {...props}
 *                      render={StoryPageBase}
 *                      loadItems={storyPageLoadItems}
 *                      onItemFocus={(item) => console.log(`Story In View: ${item.story.headline}`)}
 *                      doNotChangeUrl={true} />
 * ```
 *
 * #### Configuring the the url to change
 * When a story is focussed, the url is changed to the original slug of the story by default. To configure this, pass a prop called changeUrlTo as a function which returns the desired url.
 * This is typically used when you want to change the url but not to the original slug.
 *
 * Example:
 * ```javascript
 *   <InfiniteStoryBase {...props}
 *                      render={StoryPageBase}
 *                      loadItems={storyPageLoadItems}
 *                      onItemFocus={(item) => console.log(`Story In View: ${item.story.headline}`)}
                        changeUrlTo={(item) => item.currentPath || props.currentPath}
 *                      doNotChangeUrl={true} />
 * ```
 *
 * @component
 * @category Story Page
 */

export var InfiniteStoryBase = /*#__PURE__*/function (_React$Component) {
  _inherits(InfiniteStoryBase, _React$Component);

  var _super = /*#__PURE__*/_createSuper(InfiniteStoryBase);

  function InfiniteStoryBase(props) {
    var _this;

    _classCallCheck(this, InfiniteStoryBase);

    _this = _super.call(this, props);
    _this.state = {
      moreItems: [],
      loading: false,
      pageNumber: 0,
      seenStoryIds: [props.data.story.id]
    };
    return _this;
  }

  _createClass(InfiniteStoryBase, [{
    key: "allItems",
    value: function allItems() {
      return [this.props.data].concat(this.state.moreItems);
    }
  }, {
    key: "onFocus",
    value: function onFocus(index) {
      var _this2 = this;

      var item = this.allItems()[index];

      if (!this.props.doNotChangeUrl) {
        var storyPath = item.story.url ? new URL(item.story.url).pathname : "/" + item.story.slug;
        var metaTitle = get(item, ['story', 'seo', 'meta-title'], item.story.headline);
        var title = get(item, ["customSeo", "title"], metaTitle);
        var path = this.props.changeUrlTo ? this.props.changeUrlTo(item) : storyPath;
        global.app.maybeSetUrl(path, title);
      }

      this.props.onItemFocus && this.props.onItemFocus(item, index);

      if (!this.state.seenStoryIds.includes(item.story.id)) {
        this.setState({
          seenStoryIds: this.state.seenStoryIds.concat([item.story.id])
        }, function () {
          _this2.props.onInitialItemFocus && _this2.props.onInitialItemFocus(item, index);
        });
      }
    }
  }, {
    key: "loadMore",
    value: function loadMore() {
      var _this3 = this;

      if (this.state.loading) return;
      var pageNumber = this.state.pageNumber;
      var story = get(this.props.data, ['story'], {});
      this.setState({
        loading: true,
        pageNumber: pageNumber + 1
      }, function () {
        _this3.props.loadItems(pageNumber, story, _this3.props.config).then(function (items) {
          _this3.setState({
            loading: false,
            moreItems: _this3.state.moreItems.concat(removeDuplicateStories(_this3.allItems(), items, function (item) {
              return item.story.id;
            }))
          });
        });
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this4 = this;

      return /*#__PURE__*/React.createElement(InfiniteScroll, {
        render: this.props.render,
        items: this.allItems(),
        loadNext: function loadNext() {
          return _this4.loadMore();
        },
        loadMargin: this.props.loadMargin,
        focusCallbackAt: this.props.focusCallbackAt || 20,
        onFocus: function onFocus(index) {
          return _this4.onFocus(index);
        },
        neverHideItem: this.props.neverHideItem
      });
    }
  }]);

  return InfiniteStoryBase;
}(React.Component);